import {
  Bell,
  ChartLine,
  Eye,
  EyeSlash,
  GlobeHemisphereWest,
  LockSimple,
  MoonStars,
  ShieldCheck,
  UserCircle,
  UserGear,
  UserSound,
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { moderation, push as pushApi, users as usersApi } from '../../api/endpoints';
import {
  enregistrerCetAppareil,
  notificationsCoupeesParLeTelephone,
} from '../../native/notifications';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Button } from '../../components/ui/Button';
import { Segmented, SettingGroup, SettingRow } from '../../components/ui/Bits';
import { ConfirmDialog, useConfirm } from '../../components/ui/Confirm';
import { useAsync } from '../../hooks/useAsync';
import { useSession } from '../../store/SessionContext';
import type { AmbiancePreference } from '../../theme/ambiance';
import { t } from '../../i18n';

/**
 * Reglages du compte.
 *
 * Les interrupteurs basculent tout de suite et previennent le serveur
 * ensuite : un reglage qui attend le reseau donne l'impression d'etre
 * casse. En cas d'echec on recharge, ce qui remet la verite du serveur.
 */
/** Les trois apparences possibles, et ce que chacune promet. */
const APPARENCES: { value: AmbiancePreference; label: string }[] = [
  { value: 'system', label: t.profil.reglages.apparences.system },
  { value: 'day', label: t.profil.reglages.apparences.day },
  { value: 'night', label: t.profil.reglages.apparences.night },
];

const APPARENCE_DESCRIPTIONS: Record<AmbiancePreference, string> = {
  system: t.profil.reglages.apparenceAide.system,
  day: t.profil.reglages.apparenceAide.day,
  night: t.profil.reglages.apparenceAide.night,
};

export function SettingsScreen() {
  /*
    Mon role de moderation, s'il y en a un. Une lecture d'une seule
    valeur, sur un chemin que chacun ne peut lire que pour soi.

    Avec une cle de cache, comme le reste de l'ecran : sans elle, elle
    repartait de rien a chaque retour d'un sous-ecran, et l'entree
    « Moderation » disparaissait le temps de la relecture pour revenir
    aussitot — un clignotement au milieu d'une liste par ailleurs immobile.
  */
  const { data: role } = useAsync(() => moderation.myRole(), [], 'mon-role');
  const { server, logout, ambiancePreference, setAmbiancePreference } = useSession();
  const { data, loading, error, reload } = useAsync(
    () => usersApi.settings(),
    [],
    'settings',
  );

  const { pending, request, cancel, confirm } = useConfirm();
  // Deux gestes qu'on ne repete pas : le bouton se ferme pendant qu'ils
  // se jouent, et rouvre si le serveur refuse.
  const [busy, setBusy] = useState(false);
  const [fatal, setFatal] = useState<string | null>(null);

  /*
    Les notifications coupees dans les reglages du telephone.

    L'ecran des notifications de l'application n'y peut rien : ses
    interrupteurs trient ce qui part, pas ce que le telephone accepte
    d'afficher. Quand le telephone refuse, la ligne mene donc a ses
    reglages a lui.

    Relu au retour au premier plan, puisque c'est de la qu'on revient
    apres les avoir rallumees. L'appareil est alors reenregistre : tant
    qu'il refusait, il n'avait peut-etre jamais donne de jeton, et les
    notifications n'arriveraient qu'au prochain lancement.
  */
  const navigate = useNavigate();
  const [coupees, setCoupees] = useState(false);
  useEffect(() => {
    let avant = false;
    const relire = () => {
      if (document.visibilityState !== 'visible') return;
      void notificationsCoupeesParLeTelephone().then((maintenant) => {
        if (avant && !maintenant) void enregistrerCetAppareil();
        avant = maintenant;
        setCoupees(maintenant);
      });
    };
    relire();
    document.addEventListener('visibilitychange', relire);
    return () => document.removeEventListener('visibilitychange', relire);
  }, []);

  /*
    Se deconnecter ne navigue nulle part.

    C'est ce que faisait l'ancien code, et c'etait la panne : `logout()`
    n'etait pas attendu, si bien qu'au moment d'aller sur la connexion la
    session existait encore. Le garde y voyait quelqu'un de connecte et
    renvoyait vers le feed ; la session tombait juste apres, le garde du
    feed renvoyait vers la connexion. Deux redirections croisees, toutes
    deux en `replace`, et un ecran vide au bout.

    Une fois la session partie, chaque garde sait deja ou envoyer : il
    n'y a rien a lui dire.
  */
  async function onLogout() {
    setBusy(true);
    setFatal(null);
    try {
      await logout();
    } catch {
      // Un echec muet laisserait croire a un ecran fige.
      setBusy(false);
      setFatal(t.profil.reglages.deconnexionImpossible);
    }
  }

  /** Efface le compte, puis deconnecte : les gardes font le reste. */
  async function onDelete() {
    setBusy(true);
    setFatal(null);
    try {
      await usersApi.deleteAccount();
    } catch (cause) {
      setBusy(false);
      setFatal(cause instanceof Error ? cause.message : t.profil.reglages.suppressionImpossible);
    }
  }

  return (
    <SubScreen
      title={t.profil.commun.parametres}
      /* Posee dans le contenu, la fenetre s'ouvrirait sous le pli. */
      overlay={<ConfirmDialog pending={pending} onCancel={cancel} onConfirm={confirm} />}
    >
      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}

      {data && (
        <>
          <SettingGroup title={t.profil.reglages.compte}>
            <SettingRow
              icon={<UserCircle size={22} />}
              label={t.profil.commun.personnaliser}
              to="/app/profile/settings/account"
            />
            <SettingRow
              icon={<LockSimple size={22} />}
              label={t.profil.reglages.emailEtMotDePasse}
              to="/app/profile/settings/password"
            />
            <SettingRow
              icon={<GlobeHemisphereWest size={22} />}
              label={t.profil.reglages.serveur}
              value={server?.name}
              to="/choose-spot"
            />
          </SettingGroup>

          <SettingGroup title={t.profil.reglages.confidentialite}>
            {/* Les membres bloques s'ouvrent depuis "Visibilite du profil" :
                deux portes vers le meme ecran n'aidaient personne. */}
            <SettingRow
              icon={<Eye size={22} />}
              label={t.profil.commun.visibilite}
              to="/app/profile/settings/visibility"
            />
            {/*
              Coupees dans le telephone, la ligne ouvre ses reglages : les
              interrupteurs de l'application n'y changeraient rien. Si la
              fiche ne s'ouvre pas, on retombe sur l'ecran habituel.
            */}
            <SettingRow
              icon={<Bell size={22} />}
              label={t.profil.notifications.titre}
              description={coupees ? t.profil.reglages.coupees : undefined}
              {...(coupees
                ? {
                    onClick: () =>
                      void pushApi.ouvrirLesReglagesDuTelephone().then((ouverte) => {
                        if (!ouverte) navigate('/app/profile/settings/notifications');
                      }),
                  }
                : { to: '/app/profile/settings/notifications' })}
            />
            {/*
              Ce qu'on a ecarte de sa vue : comptes masques, comptes
              bloques, posts masques. Une seule porte vers les trois
              listes, pour pouvoir revenir sur chacune.
            */}
            <SettingRow
              icon={<EyeSlash size={22} />}
              label={t.profil.masques.titre}
              to="/app/profile/settings/masques"
            />

            {/* La file de moderation, pour qui la tient. Une entree absente
                vaut mieux qu'une entree grisee : personne n'a a savoir
                qu'elle existe. */}
            {role && (
              <SettingRow
                icon={<ShieldCheck size={22} />}
                label={t.profil.reglages.moderation}
                to="/app/profile/moderation"
              />
            )}

            {/* Le tableau de bord : ce qui tourne, en quatre nombres. Reserve
                a l'administration — il lit la fiche de tout le monde. */}
            {role === 'admin' && (
              <SettingRow
                icon={<ChartLine size={22} />}
                label={t.profil.reglages.tableau}
                to="/app/profile/tableau"
              />
            )}

            {/* L'administration se prend par le pseudo, la moderation par
                le signalement : deux entrees pour deux facons de chercher. */}
            {role && (
              <SettingRow
                icon={<UserGear size={22} />}
                label={t.profil.reglages.administration}
                to="/app/profile/administration"
              />
            )}
          </SettingGroup>

          <SettingGroup title={t.profil.reglages.application}>
            {/*
              Trois choix plutot qu'un interrupteur : « Automatique »
              suit le reglage d'apparence de l'iPhone et bascule avec
              lui, sans quitter l'application. Les deux autres forcent,
              pour qui veut la nuit en plein midi.
            */}
            <SettingRow
              icon={<MoonStars size={22} />}
              label={t.profil.reglages.apparence}
              description={APPARENCE_DESCRIPTIONS[ambiancePreference]}
            />
            <div className="setting-row__choice">
              <Segmented
                value={ambiancePreference}
                options={APPARENCES}
                onChange={setAmbiancePreference}
              />
            </div>
            {/* La langue a sa page, comme la moderation : un choix qu'on fait
                une fois n'a pas a s'etaler dans les reglages. */}
            <SettingRow
              icon={<UserSound size={22} />}
              label={t.profil.reglages.langue}
              to="/app/profile/settings/langue"
            />
            <SettingRow label={t.profil.commun.aide} to="/app/profile/settings/help" />
            {/*
              La ligne n'etait qu'un mot, sans rien derriere : elle
              annoncait une action qui n'existait pas. Elle mene
              maintenant aux trois documents et a la version acceptee.
            */}
            <SettingRow label={t.legal.ecran.titre} to="/app/profile/settings/legal" />

          </SettingGroup>

          <div className="pr-logout">
            <Button
              variant="danger"
              loading={busy}
              onClick={() => request(t.profil.reglages.deconnecterQ, () => void onLogout())}
            >
              {t.profil.reglages.deconnecter}
            </Button>
          </div>

          {fatal && <p className="pr-fatal">{fatal}</p>}

          <p className="pr-version">
            Sirocco {data.appVersion} ·{' '}
            {/*
              C'etait un simple mot, sans rien derriere. Il annonçait une
              action qui n'existait pas.
            */}
            <button
              className="pr-version__danger"
              disabled={busy}
              onClick={() =>
                request(
                  t.profil.reglages.supprimerQ,
                  () => void onDelete(),
                )
              }
            >
              {t.profil.reglages.supprimer}
            </button>
          </p>
        </>
      )}
    </SubScreen>
  );
}
